import * as React from 'react';

import {
    BLOCK_UPDATES_FORMATS,
    type BlockUpdateData,
    type BlockUpdatesDateFormat,
} from '@gitbook/doc-core';
import { createPalette, PaletteCommandType, PalettePositionWidth } from '@gitbook/react-uikit';
import type { BlockProps } from '@gitbook/slate-react';
import { formatDateFull, formatDateShort, formatNumericDate } from '@gitbook/util-format';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { BlockWrapper, useBlockPaletteFactory } from '../common';
import { UpdatesFormatContext } from './BlockUpdates';
import { UpdatesDateMenu } from './UpdatesDateMenu';

export function BlockUpdate(props: BlockProps): React.ReactElement {
    const { node, editor } = props;
    const data = node.data.toFrozenObject<BlockUpdateData>();
    const savedDateIso = data.date;
    const palette = useBlockPaletteFactory({ nodeKey: node.key });
    const [selectedDate, setSelectedDate] = React.useState(() =>
        savedDateIso ? new Date(savedDateIso) : new Date()
    );

    // Get format from parent Updates block via context
    const formatContext = React.useContext(UpdatesFormatContext);
    const format = formatContext?.format ?? 'full';
    const updateFormat = formatContext?.updateFormat;

    const formattedDate = formatLabel(format, selectedDate);

    const dateContainerRef = React.useRef<HTMLDivElement>(null);

    const handleDateChange = useEventCallback((date: Date) => {
        setSelectedDate(date);
        editor.change((change) => {
            change.setNodeByKey(node.key, {
                data: node.data.set('date', date.toISOString()),
            });
        });
    });

    const formatPalette = React.useCallback(
        () =>
            createPalette({
                groups: [
                    {
                        title: 'Date format',
                        key: 'date-format',
                        commands: BLOCK_UPDATES_FORMATS.map((option) => ({
                            key: option,
                            title: formatLabel(option, selectedDate),
                            type: PaletteCommandType.RADIO,
                            selected: option === format,
                            trigger: () => {
                                updateFormat?.(option);
                            },
                        })),
                    },
                ],
                position: {
                    width: PalettePositionWidth.SMALL,
                },
            }),
        [format, selectedDate, updateFormat]
    );

    return (
        <BlockWrapper {...props} palette={palette}>
            <div className="flex gap-4 md:gap-8">
                <div contentEditable={false} className="flex w-40 min-w-40 shrink-0 flex-col">
                    <div className="sticky top-4" ref={dateContainerRef}>
                        {editor.readOnly ? (
                            <div className="text-muted text-ui-base">{formattedDate}</div>
                        ) : (
                            <UpdatesDateMenu
                                formattedDate={formattedDate}
                                value={selectedDate}
                                onChange={handleDateChange}
                                createFormatPalette={formatPalette}
                            />
                        )}
                    </div>
                </div>
                <div className="flex-1">{props.children}</div>
            </div>
        </BlockWrapper>
    );
}

function formatLabel(format: BlockUpdatesDateFormat, date: Date): string {
    switch (format) {
        case 'full':
            return formatDateFull(date);
        case 'short':
            return formatDateShort(date);
        case 'numeric':
            return formatNumericDate(date);
        default:
            assertNever(format);
    }
}
